import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { Spinner } from "./components/ui";
import { AuthProvider, useAuth } from "./lib/auth";
import { AccountsPage } from "./pages/Accounts";
import { AssistantPage } from "./pages/Assistant";
import { CommitteeDetailPage, CommitteesPage } from "./pages/Committees";
import { ConnectionsPage } from "./pages/Connections";
import { PersonDetailPage } from "./pages/PersonDetail";
import { DashboardPage } from "./pages/Dashboard";
import { ForecastPage } from "./pages/Forecast";
import { InsightsPage } from "./pages/Insights";
import { LoanRespondPage } from "./pages/LoanRespond";
import { LoansPage } from "./pages/Loans";
import { LoanDetailPage } from "./pages/LoanDetail";
import { NotificationsPage } from "./pages/Notifications";
import { SettingsPage } from "./pages/Settings";
import { TransactionsPage } from "./pages/Transactions";
import { WitnessConfirmPage } from "./pages/WitnessConfirm";
import { LandingPage } from "./pages/Landing";
import {
  ForgotPasswordPage,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from "./pages/auth";

function Protected({ children }: { children: JSX.Element }): JSX.Element {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }: { children: JSX.Element }): JSX.Element {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          {/* Public auth pages */}
          <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          {/* Public token pages */}
          <Route path="/witness/confirm" element={<WitnessConfirmPage />} />
          <Route path="/loan/respond" element={<LoanRespondPage />} />

          {/* Authenticated app */}
          <Route
            element={
              <Protected>
                <AppShell />
              </Protected>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/transactions" element={<TransactionsPage />} />
            <Route path="/loans" element={<LoansPage />} />
            <Route path="/loans/:id" element={<LoanDetailPage />} />
            <Route path="/committees" element={<CommitteesPage />} />
            <Route path="/committees/:id" element={<CommitteeDetailPage />} />
            <Route path="/connections" element={<ConnectionsPage />} />
            <Route path="/connections/:id" element={<PersonDetailPage />} />
            <Route path="/witness" element={<Navigate to="/connections?view=witness" replace />} />
            <Route path="/forecast" element={<ForecastPage />} />
            <Route path="/assistant" element={<AssistantPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            {/* Profile moved into Settings as a tab; keep the path as a redirect. */}
            <Route path="/profile" element={<Navigate to="/settings" replace />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
